import React, { useEffect, useState, useCallback, useRef } from "react";
import classNames from "classnames";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { useTranslation, Trans } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CountryActions from "../../Redux/actions/country-actions";
import validator from "validator";
import { getLogin } from "../../Redux/reducers/account-reducer";
import SignupActions from "../../Redux/actions/signup-actions";
import { setAuthToken, removeAuthToken } from "../../Service/api";
import { PhoneNumberInputText } from "../Subscriptions/Invitation/InviteComponents/PhoneNumberInputText";
import { isValidInternationalPhoneNumber, normalizePhoneNumber, toLocalPhoneNumber } from "../../utils/phoneUtils";
import "../../Styles/pages/configure.scss";
import Loading from "../../Components/Loading";
import "../../Styles/pages/sendMoney.scss";
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import BusinessActions from '../../Redux/actions/business-actions';
import { useKeycloak } from "@react-keycloak/web";
import { removeTrailingSpacesAndDashes, filterCharacters } from "../../Components/CleanInputs";

const ConfigureBusiness = ({ location }) => {

	const navigate = useNavigate();
	const { t } = useTranslation("common");
	const toast = useRef(null);

	const [formData, setFormData] = useState({
		name: '',
		shortName: '',
		repName: '',
		repRole: '',
		signerEmail: '',
	});

	const [errors, setErrors] = useState({
		name: false,
		shortName: false,
		repName: false,
		repRole: false,
	});

	const [state, setState] = useState({
		activeTab: "register",
		activeCard: "",
		countriesData: [],
		country: null,
		code: null,
		iso2: null,
		dialCode: "",
		countryCode: "",
		phoneTouched: false,
		phoneNoErrorMessage: null,
		regions: [],
		regionStates: [],
		regionState: null,
		purpose: "",
		name: "",
		city: "",
		phoneNo: "",
		email: "",
		addressLine1: "",
		addressLine2: "",
		nameError: false,
		regionStateError: false,
		addressLine1Error: false,
		emailError: false,
		purposeError: false,
		cityError: false,
		industryTypeError: false,
		requesting: false,
		firstSubmit: false,
		newList: [],
		update: false,
		industryType: false,
		postcode: "",
		type: "ORGANISATION",
		industries: [],
		shortName: "",
		shortNameError: false,
		repNameError: false,
		repRoleError: false,
		signerEmailError: false

	});

	const types = [
		{ label: t("business.title"), value: "ORGANISATION" },
		//might be in use later
		// { label: t("Individual"), value: "INDIVIDUAL" },
		// { label: t("Organisation"), value: "ORGANISATION" },
	];

	//Redux State from Store
	const countries = useSelector((state) => state.countries.countries);
	const signupBusiness = useSelector((state) => state.signup.business);
	const updatingSign = useSelector((state) => state.signup.updating);
	const errorUpdatingSign = useSelector((state) => state.signup.errorUpdating);
	const login = useSelector((state) => getLogin(state.account));
	const updatingSuccess = useSelector((state) => state.signup.business);
	const industries = useSelector((state) => state.businesses.industries);

	//Redux Dispatch Actions
	const dispatch = useDispatch();
	const getAllCountries = useCallback((options) => { dispatch(CountryActions.countryAllRequest(options)) }, [dispatch]);
	const configureBusiness = (business) => { dispatch(SignupActions.signupUpdateRequest(business)) };
	const fetchCountries = useCallback(() => { getAllCountries() }, [getAllCountries]);
	const getIndustries = useCallback((industry) => { dispatch(BusinessActions.businessIndustriesRequest(industry)) }, [dispatch])

	const { keycloak } = useKeycloak();
	const logoutFn = async () => {
		removeAuthToken()
		keycloak.logout({
			redirectUri: process.env.REACT_APP_AUTH_LOGOUTURL,
		});
		sessionStorage.setItem('crumbs', [])
	};

	const handleToken = useCallback(async () => {
		try {
			await keycloak.updateToken(60)
				.then(() => {
					const token = keycloak.token;
					setAuthToken(token);
				})
				.then(() => {
					fetchCountries()
				})

		} catch (e) {
			keycloak.logout({
				redirectUri: process.env.REACT_APP_AUTH_LOGOUTURL,
			});
		}
	}, [keycloak, fetchCountries]);

	useEffect(() => {
		handleToken();
	}, [handleToken]);

	//Handle side effects related to country list
	useEffect(() => {
		if (countries) {
			let countryList = [];
			countries.forEach((element) => {
				const countryObject = {};
				countryObject.label = element.name;
				countryObject.value = element.id;
				countryObject.code = element.phoneCode;
				countryObject.iso2 = element.code;
				countryObject.regions = element.regions;
				countryList.push(countryObject);
			});
			setState((prev) => { return { ...prev, countriesData: countryList }; });
		}
	}, [countries])

	//Handle side effects for regions
	useEffect(() => {
		if (state.regions) {
			let regions = []
			state.regions.forEach((ele) => {
				const regionObject = {}
				regionObject.label = ele.name;
				regionObject.value = ele.name;
				regions.push(regionObject)
			})
			setState((state) => ({ ...state, newList: regions }))
		}
	}, [state.regions])

	useEffect(() => {
		if (countries) {
			getIndustries("all")
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countries])

	//Handle side effects for submission of uploaded IDs for review
	useEffect(() => {
		if (!updatingSign && state.update && updatingSuccess) {
			showSuccess();
			setState(state => { return { ...state, update: false } })
		}
		if (!updatingSign && errorUpdatingSign && state.update) {
			setState(state => { return { ...state, update: false } })
			showError();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updatingSign, errorUpdatingSign, state.update, updatingSuccess]);

	if (!countries) {
		return <div style={{ height: "100vh" }}><Loading /></div>
	}

	const {
		nameError, emailError, regionStateError, addressLine1Error, cityError, industryTypeError, countryError, type,
		addressLine1, purpose, regionState, email, phoneNo, city, purposeError, firstSubmit, country, countriesData, industryType,
		shortNameError, repNameError, repRoleError, signerEmailError, iso2, dialCode, countryCode, phoneTouched, phoneNoErrorMessage
	} = state;

	const validPhone = isValidInternationalPhoneNumber(phoneNo, dialCode, countryCode);
	const phoneNoError = !!phoneNoErrorMessage && (firstSubmit || phoneTouched);

	const showSuccess = () => {
		toast.current.show({ severity: 'success', summary: t("success"), detail: t("business.success_message"), life: 3000 });
	};

	const showError = () => {
		toast.current.show({ severity: 'error', summary: t("failed"), detail: t("business.error_message"), life: 4000 });
	};

	// PhoneNumberInputText is written against Formik; this maps its calls onto the page's useState.
	const phoneFormik = {
		values: { number: phoneNo, dialCode, countryCode },
		touched: { number: phoneTouched },
		errors: { number: phoneNoErrorMessage },
		setFieldValue: (name, value) => setState((prev) => {
			const key = name === "number" ? "phoneNo" : name;
			if (prev[key] === value) return prev;
			return { ...prev, [key]: value };
		}),
		setFieldError: (name, message) => setState((prev) => {
			const next = message ?? null;
			if (prev.phoneNoErrorMessage === next) return prev;
			return { ...prev, phoneNoErrorMessage: next };
		}),
		setFieldTouched: (name, touched = true) => setState((prev) => {
			if (prev.phoneTouched === touched) return prev;
			return { ...prev, phoneTouched: touched };
		}),
	};

	const isValid = () => {
		const validEmail = validator.isEmail(email);
		const validSignerEmail = validator.isEmail(formData.signerEmail || "");
		setState((prev) => {
			return {
				...prev,
				nameError: !formData.name || formData.name.trim() === "",
				shortNameError: !formData.shortName || formData.shortName.trim() === "",
				repNameError: !formData.repName || formData.repName.trim() === "",
				repRoleError: !formData.repRole || formData.repRole.trim() === "",
				signerEmailError: !formData.signerEmail || formData.signerEmail.trim() === "" || !validSignerEmail,
				addressLine1Error: !addressLine1 || addressLine1.trim() === "",
				cityError: !city || city.trim() === "",
				purposeError: !purpose,
				typeError: !state.type,
				regionStateError: !regionState,
				emailError: !email || email.trim() === "" || !validEmail,
				phoneNoErrorMessage: validPhone ? null : t("business.error_phone_invalid"),
				countryError: !country,
				industryTypeError: !industryType
			};
		});

		if (!formData.name || formData.name.trim() === "" || !formData.shortName || formData.shortName.trim() === "" || !addressLine1
			|| addressLine1.trim() === "" || !city || city.trim() === "" ||
			!purpose || !regionState || !email || !type || email.trim() === "" ||
			!validEmail || !validPhone || !industryType ||
			!formData.repName || formData.repName.trim() === "" ||
			!formData.repRole || formData.repRole.trim() === "" ||
			!formData.signerEmail || formData.signerEmail.trim() === "" || !validSignerEmail
		) {
			return false;
		}
		return true;
	};

	const handleChange = (e) => {

		const { name, value } = e.target;
		const cleanedValue = value.replace(filterCharacters.pattern, filterCharacters.replacement);

		setFormData((prev) => ({
			...prev,
			[name]: cleanedValue
		}));
		const hasInvalidChars = filterCharacters.pattern.test(value);
		setErrors((prev) => ({
			...prev,
			[name]: hasInvalidChars ? t("common.allowed_characters") : ''
		}));
		if (firstSubmit) {
			isValid();
		}
	};

	// Email must not go through filterCharacters, which strips "@" and "."
	const handleSignerEmailChange = ({ target }) => {
		setFormData((prev) => ({ ...prev, signerEmail: target.value }));
		if (firstSubmit) {
			isValid();
		}
	};

	const onSubmit = () => {
		setState((prev) => {
			return { ...prev, firstSubmit: true };
		});
		if (!isValid()) {
			return;
		}

		const normalizedPhone = normalizePhoneNumber(phoneNo, dialCode, countryCode);
		if (!normalizedPhone || !isValidInternationalPhoneNumber(normalizedPhone)) {
			setState((prev) => ({ ...prev, phoneTouched: true, phoneNoErrorMessage: t("business.error_phone_invalid") }));
			return;
		}

		const business = {
			name: removeTrailingSpacesAndDashes(formData.name),
			shortName: removeTrailingSpacesAndDashes(formData.shortName),
			phoneNo: toLocalPhoneNumber(normalizedPhone),
			country: { id: state.country },
			addressLine1: addressLine1,
			addressLine2: state.addressLine2,
			city: city,
			type: types[0].value,
			email: email,
			purpose: purpose,
			login: login,
			regionState: regionState,
			industryId: industryType.id,
			postcode: state.postcode,
			signerFullName: removeTrailingSpacesAndDashes(formData.repName),
			signerCapacity: removeTrailingSpacesAndDashes(formData.repRole),
			signerEmail: formData.signerEmail.trim(),
		};
		if (business) {
			setState((prev) => {
				return { ...prev, requesting: true, loading: true, update: true };
			});
			configureBusiness(business);
		}
	};

	if (
		!updatingSign &&
		!errorUpdatingSign &&
		!!signupBusiness &&
		state.loading &&
		state.requesting
	) {
	}

	const goToDashboard = () => {
		navigate("/home", { state: window.location.href })
	};

	return (
		<div className="wizard-body back" >
			<Toast ref={toast} onRemove={(message) => {
				if (message.severity === "success") {
					localStorage.setItem("onboardingDialog", true);
					goToDashboard();
				}
			}}
			></Toast>
			<div className="wizard-wrapper">
				<div className="wizard-content">
					<div className="wizard-card" style={{ width: "70%" }}>
						<div className={classNames("wizard-card-content register active-content main-business-card")}>
							<div className="wizard-card-header">
								<div className="wizard-card-header-banner">
									<div className='business-profile-content'>
										<span className="create_business_account">{t("business.create_business")}</span>
									</div>
								</div>
								{<div className="wizard-forms-wrapper p-grid p-nogutter">
									<div className="business_profile_info">
										<p className="business_header_text">{t("business.create")}</p>
										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="name"
													className="form-label"
													style={{ color: nameError ? "#D0021B" : "#000000" }}>
													{nameError ? t("business.error_name_required")
														: state.type === "ORGANISATION" ? t("business.organisation_name") : t("business.organisation_name")}
													<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<InputText
													name="name"
													value={formData.name}
													className={errors.name ? 'p-invalid' : 'p-error business_inputs'}
													id="business_name"
													placeholder={t("business.name_placeholder")}
													onChange={handleChange}
													maxLength={50}
												/>
												{errors.name ? (
													<p className="character_hint">{errors.name}</p>
												) : <div className="doc_tag_container">
													<Trans
														i18nKey="business.businessName_hint"
														components={{
															1: <span className="doc_tag" />,
															2: <span className="doc_tag_bold"></span>,
															3: <span className="doc_tag"></span>
														}}
													>
													</Trans>
												</div>}
											</div>

											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="shortName"
													className="form-label"
													style={{ color: shortNameError ? "#D0021B" : "#000000" }}>
													{shortNameError ? t("business.error_shortName_required") : t("business.shortNameLabel")}
													<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<InputText
													name="shortName"
													value={formData.shortName}
													className={errors.shortName ? 'p-invalid' : ''}
													id="business_shortName_required"
													placeholder={t("business.shortNamePlaceholder")}
													onChange={handleChange}

													maxLength={15}
												/>
												{errors.shortName ? (
													<p className="character_hint">
														{t("common.allowed_characters")}
													</p>
												) : <div className="shortNameHint-div">
													<Trans
														i18nKey="business.shortName_hint"
														components={{
															1: <span className="shortNameHint-bold" />,
															2: <span className="shortNameHint"></span>
														}}
													>
													</Trans>
												</div>}
											</div>
										</div>

										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms back">
												<label htmlFor="type"
													className="form-label back"
													style={{ color: "#000000" }}>
													{t("business.merchant_type")}
												</label>
												<InputText
													disabled
													id="merchant_type"
													value={types.label}
													placeholder={t("business.title")}
													className="drop_down business_inputs"
													onChange={(event) => { setState((prev) => { return { ...prev, type: event.value } }) }}
												/>
											</div>
											{industries && <div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="name"
													className="form-label"
													style={{ color: industryTypeError ? "#D0021B" : "#000000" }}>
													{industryTypeError ? t("business.error_industry_required") : t("business.industry_type")}<span style={{ color: "#D0021B" }}>*</span>
												</label>

												<Dropdown
													id="business_type"
													options={[...industries].sort((a, b) => a.nameEn?.localeCompare(b.nameEn))}
													optionLabel="nameEn"
													value={state.industryType}
													placeholder={t("business.select_industry")}
													required
													className="drop_down business_inputs"
													onChange={(event) => { setState((prev) => { return { ...prev, industryType: event.value } }) }}
												/>

											</div>}
										</div>
										<hr className="first_hr_business" />

										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="country"
													className="form-label"
													style={{ color: countryError ? "#D0021B" : "#000000" }}>
													{countryError ? t("business.error_country_required") : t("business.country")}<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<Dropdown
													id="country_business"
													name="country"
													options={countriesData.sort((a, b) => (a.label < b.label ? -1 : 1))}
													value={country}
													placeholder={t("business.choose_country")}
													required
													onChange={(event) =>
														setState((prev) => {
															let code;
															let iso2;
															let regions;
															countriesData.map((ele) => {
																if (ele.value === event.value) {
																	code = ele.code;
																	iso2 = ele.iso2;
																	regions = ele.regions
																}
																return null;
															});
															return {
																...prev, country: event.value, code, iso2, regions,
																dialCode: code || "",
																countryCode: (iso2 || "").toUpperCase(),
																// the typed number belongs to the previous country's dial code
																phoneNo: "",
																phoneTouched: false,
																phoneNoErrorMessage: null,
															};
														})
													}
												/>
											</div>
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="business_address1-required"
													className="form-label"
													style={{ color: addressLine1Error ? "#D0021B" : "#000000" }}>
													{addressLine1Error ? t("business.error_address1_required") : t("business.address_1")}<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<InputText
													id="business_address1-required"
													value={addressLine1}
													placeholder={t("business.address1")}
													onChange={({ target }) => {
														if (firstSubmit) {
															isValid();
														}
														setState((prev) => { return { ...prev, addressLine1: target.value } });
													}}
												/>
											</div>
										</div>
										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="business_address2-required"
													className="form-label"
													style={{ color: "#000000" }}
												>{t("business.address_2")}
												</label>
												<InputText
													id="business_address2-required"
													className="business_inputs"
													placeholder={t("business.address2")}
													value={state.addressLine2}
													onChange={({ target }) =>
														setState((prev) => { return { ...prev, addressLine2: target.value, } })}
												/>
											</div>

											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="postcode"
													className="form-label"
													style={{ color: "#000000" }}
												>{t("business.postcode")}
												</label>
												<InputText
													id="business_postcode"
													placeholder={t("business.postcode_placeholder")}
													value={state.postcode}
													onChange={({ target }) =>
														setState((prev) => { return { ...prev, postcode: target.value, } })}
												/>
											</div>
										</div>
										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="regionState"
													className="form-label"
													style={{ color: regionStateError ? "#D0021B" : "#000000" }}>
													{regionStateError ? t("business.error_region_state_required") : t("business.choose_region")}<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<Dropdown
													id="regionState"
													options={[...state.newList].sort((a, b) => (a.label < b.label ? -1 : 1))}
													value={regionState}
													placeholder={t("business.region_placeholder")}
													required
													className="drop_down business_inputs"
													onChange={(event) =>
														setState((prev) => { return { ...prev, regionState: event.value } })}
												/>
											</div>
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
												<label
													htmlFor="business_city-required"
													className="form-label"
													style={{ color: cityError ? "#D0021B" : "#000000" }}>
													{cityError ? t("business.error_city_required") : t("business.city")}<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<InputText
													id="business_city"
													value={city}
													placeholder={t("business.city_placeholder")}
													onChange={({ target }) => {
														if (firstSubmit) {
															isValid();
														}
														setState((prev) => { return { ...prev, city: target.value } });
													}}
												/>
											</div>
										</div>
										<div className="grid">
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms ">
												<PhoneNumberInputText
													key={iso2 || "no-country"}
													formik={phoneFormik}
													defaultCountry={iso2 ? iso2.toLowerCase() : "gh"}
													disabled={!country}
													placeholder={t("business.phone_placeholder")}
													containerClassName="configure_phone_input"
													isFormFieldValid={() => phoneNoError}
													getFormErrorMessage={() => null}
													errorMessages={{
														required: t("business.error_phone_invalid"),
														invalid: t("business.error_phone_invalid"),
													}}
													label={
														<label
															htmlFor="business_phoneNo-required"
															className="form-label"
															style={{ color: phoneNoError ? "#D0021B" : "#000000" }}>
															{phoneNoError ? <span className="error_phone_required">{t("business.error_phone_invalid")}</span> :
																<span className="phone_required"> {t("business.support_number")}</span>}
															<span style={{ color: "#D0021B" }}>*</span>
														</label>
													}
													hint={
														<div className="doc_tag_container">
															<Trans
																i18nKey="business.phone_tag_hint"
																components={{
																	1: <span className="doc_tag_bold" />,
																	2: <span className="doc_tag"></span>,
																	3: <span className="doc_tag_bold"></span>,
																}}
															>
															</Trans>
														</div>
													}
												/>
											</div>
											<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms" >
												<label
													htmlFor="email"
													className="form-label"
													style={{ color: emailError ? "#D0021B" : "#000000", marginTop: "2rem" }}>
													{emailError ? !email || email.trim() === ""
														? t("business.error_email_required") : t("business.error_email_invalid") : t("business.email_label")}<span style={{ color: "#D0021B" }}>*</span>
												</label>
												<InputText
													id="business_profile_email"
													placeholder={t("business.email")}
													className={` p-error ${emailError ? "p-error" : ""}`}
													onChange={({ target }) => {
														if (firstSubmit) {
															isValid();
														}
														setState((prev) => { return { ...prev, email: target.value } });
													}}
													maxLength={50}
												/>
												<div className="doc_tag_container">
													<Trans
														i18nKey="business.email_tag_hint"
														components={{
															1: <span className="doc_tag_bold" />,
															2: <span className="doc_tag"></span>,
															3: <span className="doc_tag_bold"></span>,
														}}
													>
													</Trans>
												</div>
											</div>

											<hr className="business_last_hr" />
											<div className="grid" style={{ width: "100%" }}>
												<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
													<p className="mb-0" style={{ color: "#495057", fontWeight: "bold" }}>
														{t("business.rep_section_title")}
													</p>
													<div className="grid">
														<div className="col-12 wizard-forms mt-0">
															<label
																htmlFor="rep_name"
																className="form-label"
																style={{ color: repNameError ? "#D0021B" : "#000000" }}>
																{repNameError ? t("business.error_rep_name_required") : t("business.rep_name_label")}
																<span style={{ color: "#D0021B" }}>*</span>
															</label>
															<InputText
																name="repName"
																id="rep_name"
																value={formData.repName}
																className={errors.repName ? 'p-invalid' : ''}
																placeholder={t("business.rep_name_placeholder")}
																onChange={handleChange}
																maxLength={50}
															/>
															{errors.repName && (
																<p className="character_hint">{errors.repName}</p>
															)}
														</div>
														<div className="col-12 wizard-forms">
															<label
																htmlFor="rep_role"
																className="form-label"
																style={{ color: repRoleError ? "#D0021B" : "#000000" }}>
																{repRoleError ? t("business.error_rep_role_required") : t("business.rep_role_label")}
																<span style={{ color: "#D0021B" }}>*</span>
															</label>
															<InputText
																name="repRole"
																id="rep_role"
																value={formData.repRole}
																className={errors.repRole ? 'p-invalid' : ''}
																placeholder={t("business.rep_role_placeholder")}
																onChange={handleChange}
																maxLength={50}
															/>
															{errors.repRole && (
																<p className="character_hint">{errors.repRole}</p>
															)}
														</div>
														<div className="col-12 wizard-forms">
															<label
																htmlFor="signer_email"
																className="form-label"
																style={{ color: signerEmailError ? "#D0021B" : "#000000" }}>
																{signerEmailError
																	? (!formData.signerEmail || formData.signerEmail.trim() === ""
																		? t("business.error_signer_email_required")
																		: t("business.error_signer_email_invalid"))
																	: t("business.signer_email_label")}
																<span style={{ color: "#D0021B" }}>*</span>
															</label>
															<InputText
																name="signerEmail"
																id="signer_email"
																value={formData.signerEmail}
																className={signerEmailError ? 'p-invalid' : ''}
																placeholder={t("business.signer_email_placeholder")}
																onChange={handleSignerEmailChange}
																maxLength={50}
															/>
														</div>
													</div>
												</div>
												<div className="col-12 sm:col-12 md:col-12 lg:col-6 wizard-forms">
													<p style={{ color: purposeError ? "#D0021B" : "#495057", fontWeight: "bold" }}>
														{purposeError ? t("business.account_purpose_required") : t("business.choose_purpose")}
														<span style={{ color: "#D0021B" }}>*</span>
													</p>
													<div className="p-col-12" style={{ marginBottom: "1rem" }}>
														<RadioButton
															value="SERVICES"
															inputId="rb1"
															onChange={(event) => {
																if (firstSubmit) {
																	isValid();
																}
																setState((prev) => { return { ...prev, purpose: event.value } });
															}}
															checked={purpose === "SERVICES"}
														/>
														<label
															htmlFor="rb1"
															className="p-radiobutton-label"
															style={{ color: "black" }}>
															{t("business.purpose_SERVICES")}
														</label>
													</div>
													<div className="p-col-12">
														<RadioButton
															value="DONATION"
															inputId="rb2"
															onChange={(event) => {
																if (firstSubmit) {
																	isValid();
																}
																setState((prev) => { return { ...prev, purpose: event.value } });
															}}
															checked={purpose === "DONATION"}
														/>
														<label
															htmlFor="rb2"
															className="p-radiobutton-label"
															style={{ color: "black" }}>{t("business.purpose_DONATION")}
														</label>
													</div>
												</div>
											</div>
										</div>
									</div>

									<div className="configure_buttons">
										<div style={{ width: "6.4rem" }}>
											<Button
												className="continue-button"
												label={t("Logout")}
												style={{ backgroundColor: "#D0021B" }}
												onClick={() => { logoutFn() }}
											/>
										</div>
										<div>
											<Button
												className="p-button-info create_business_btn"
												label={t("business.continue_business_button")}
												onClick={() => { onSubmit() }}
												loading={updatingSign}
											/>
										</div>
									</div>
								</div>}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div >
	);
};
export default ConfigureBusiness;